import React, { useLayoutEffect, useRef } from "react";
import { money } from "./staffpage";
import { formatDaysHours, layoutPaymentTimeline, paymentColor, paymentRunningTotal } from "./paymentTimeline";

function span(from, through) {
  if (from && through) return from + " – " + through;
  return from || through || "—";
}

function perDay(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return money(value) + "/day";
}

function Lane({ bars, height }) {
  const tracks = bars.reduce((max, bar) => Math.max(max, bar.track + 1), 1);
  return (
    <div className="pay-lane" style={{ height }}>
      {Array.from({ length: tracks }, (_, track) => (
        <div key={track} className="pay-rail" style={{ top: 6 + track * 16 }} />
      ))}
      {bars.map((bar) => (
        <span
          key={bar.row + "-" + bar.start}
          className="pay-bar"
          title={bar.title}
          style={{ left: bar.left, width: bar.width, top: bar.top, background: bar.color }}
        />
      ))}
    </div>
  );
}

function PaymentTimeline({ rows }) {
  const layout = layoutPaymentTimeline(rows);
  const scroller = useRef(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [layout && layout.width]);
  if (!layout) return null;
  return (
    <div className="pay-timeline">
      <div className="pay-timeline-names">
        <div className="pay-axis-name" />
        <div className="pay-lane-name" style={{ height: layout.rentalHeight }}>Rental</div>
        <div className="pay-lane-name" style={{ height: layout.insuranceHeight }}>Insurance</div>
      </div>
      <div className="pay-timeline-scroll" ref={scroller}>
        <div className="pay-timeline-canvas" style={{ width: layout.width }}>
          <div className="pay-axis">
            {layout.ticks.map((tick) => (
              <span key={tick.ms} className="pay-tick" style={{ left: tick.left }}>{tick.label}</span>
            ))}
          </div>
          <Lane bars={layout.rental} height={layout.rentalHeight} />
          <Lane bars={layout.insurance} height={layout.insuranceHeight} />
        </div>
      </div>
    </div>
  );
}

export default function ReservationPayments({ payments }) {
  if (!payments || payments.ready === false) {
    return <p className="staff-note">Payments did not load.</p>;
  }
  const rows = payments.rows || [];
  const total = paymentRunningTotal(rows);
  if (!rows.length) {
    return (
      <p className="pay-empty">
        No new-app charges on this reservation. History still has every version of the contract.
      </p>
    );
  }
  return (
    <div className="pay-wrap">
      <PaymentTimeline rows={rows} />
      <p className="pay-empty">Each row is a charge that went through. An unpaid quote is not listed.</p>
      <div className="pay-scroll">
        <table className="pay-table">
          <thead>
            <tr>
              <th className="pay-key-col" />
              <th>Paid</th>
              <th>What</th>
              <th>Rental dates</th>
              <th>Insurance dates</th>
              <th>Rental</th>
              <th>Insurance</th>
              <th>Charged</th>
              <th>Running</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.paidAt + row.runningUsd}>
                <td className="pay-key-col">
                  <span className="pay-swatch" style={{ background: paymentColor(index) }} />
                </td>
                <td>{row.paidAt}</td>
                <td>{row.kind}</td>
                <td>{span(row.rentalFrom, row.rentalThrough)}</td>
                <td>{span(row.insuranceFrom, row.insuranceThrough)}</td>
                <td>{money(row.rentalUsd)}</td>
                <td>{money(row.insuranceUsd)}</td>
                <td>{money(row.chargedUsd)}</td>
                <td>{money(row.runningUsd)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="pay-key-col" />
              <td>Running total</td>
              <td>—</td>
              <td>{formatDaysHours(total.rentalHours)}</td>
              <td>{formatDaysHours(total.insuranceHours)}</td>
              <td>{perDay(total.rentalPerDay)}</td>
              <td>{perDay(total.insurancePerDay)}</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
